import { ApiService } from '~/services/api/api'
import type {
  AddUsersToWorkspaceResponse,
  Workspace,
  WorkspaceIntegrations,
  WorkspaceSettingsSummary,
  WorkspaceUserEntry,
  WorkspaceWithUsers,
} from '~/types/Workspace'
import type { PaginatedResponse } from '~/types/PaginatedResponse'

export class WorkspaceService {
  private readonly apiService = new ApiService()

  async getWorkspaces(queryParameters: Record<string, string> = {}) {
    const response = await this.apiService.get<Workspace[]>('/workspaces', {
      params: queryParameters,
    })
    return response
  }

  async getWorkspaceById(workspaceId: string) {
    const response = await this.apiService.get<WorkspaceWithUsers>(
      `workspaces/${workspaceId}`,
    )
    return response
  }

  async postWorkspace(body: Partial<Workspace>) {
    const response = await this.apiService.post<Workspace, Partial<Workspace>>(
      '/workspaces',
      body,
    )
    return response
  }

  async updateWorkspaceById(workspaceId: string, body: Partial<Workspace>) {
    const response = await this.apiService.patch<Workspace, Partial<Workspace>>(
      `/workspaces/${workspaceId}`,
      body,
    )
    return response
  }

  async deleteWorkspaceById(workspaceId: string) {
    const response = await this.apiService.delete<undefined>(
      `/workspaces/${workspaceId}`,
    )
    return response
  }

  async inviteUsersToWorkspace(
    workspaceId: string,
    userEmails: string[],
    note?: string,
  ) {
    const body: { emails: string[]; note?: string } = { emails: userEmails }
    if (note) body.note = note
    const response = await this.apiService.post<
      AddUsersToWorkspaceResponse,
      typeof body
    >(`/workspaces/${workspaceId}/users/invite`, body)
    return response
  }

  // Adds existing users (by id) to a workspace — POST /workspaces/:id/users
  // { userIds }. Used by the super-admin "join workspace" action (adds self).
  async addUsersToWorkspace(workspaceId: string, userIds: string[]) {
    return await this.apiService.post<
      AddUsersToWorkspaceResponse,
      { userIds: string[] }
    >(`/workspaces/${workspaceId}/users`, { userIds })
  }

  // Every active member id — the "Add users" search excludes current members
  // client-side, so the set must be complete. A single page would leave members
  // past the limit selectable in large workspaces, then fail the add mutation,
  // so we page through until there's no next page.
  async getWorkspaceMemberIds(workspaceId: string) {
    const ids: string[] = []
    let page = 1
    while (true) {
      const response = await this.apiService.get<
        PaginatedResponse<WorkspaceUserEntry>
      >(`/workspaces/${workspaceId}/users`, {
        params: { limit: '100', page: String(page) },
      })
      if (response.error) {
        return { data: null, error: response.error }
      }
      ids.push(...(response.data?.data ?? []).map((entry) => entry.user.id))
      if (!response.data?.meta.hasNextPage) {
        break
      }
      page += 1
    }
    return { data: ids, error: null }
  }

  async removeUserFromWorkspace(workspaceId: string, userId: string) {
    const response = await this.apiService.delete<undefined>(
      `/workspaces/${workspaceId}/users/${userId}`,
    )
    return response
  }

  async getWorkspaceSummary(workspaceId: string) {
    return await this.apiService.get<WorkspaceSettingsSummary>(
      `/workspaces/${workspaceId}/summary`,
    )
  }

  async getWorkspaceIntegrations(workspaceId: string) {
    return await this.apiService.get<WorkspaceIntegrations>(
      `/workspaces/${workspaceId}/integrations`,
    )
  }
}
